// Builds the dashboard notification feed from the user's own data.
// Each item has a stable id so "read" state survives reloads (stored per user in localStorage).

const timeAgo = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const minutes = Math.round((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days} d ago`
  return date.toLocaleDateString()
}

const usd = (value) => `$${Number(value || 0).toLocaleString()}`

export const buildNotifications = (user) => {
  if (!user) return []
  const items = []

  // ── activity ──
  // recentLogins[0] is the current session; [1] is the sign-in before it
  const previousLogin = user.recentLogins?.[1]
  if (previousLogin) {
    const where = [previousLogin.browser && `${previousLogin.browser} on ${previousLogin.os}`, previousLogin.location]
      .filter(Boolean).join(' · ')
    items.push({
      id: `login-${previousLogin.date}`,
      kind: 'activity',
      tone: 'info',
      title: 'Previous sign-in',
      text: `${where || 'Unknown device'}. Not you? Change your password and turn on 2FA.`,
      time: timeAgo(previousLogin.date),
      link: '/login-history',
    })
  }

  const transactions = [...(user.transaction || [])].reverse().slice(0, 3)
  transactions.forEach(tx => {
    const type = (tx.type || 'transaction').toLowerCase()
    items.push({
      id: `tx-${tx.id || `${type}-${tx.date}`}`,
      kind: 'activity',
      tone: type === 'withdraw' || type === 'withdrawal' ? 'neutral' : 'success',
      title: `${type.charAt(0).toUpperCase()}${type.slice(1)} of ${usd(tx.amount)}`,
      text: tx.balance !== undefined ? `Balance after: ${usd(tx.balance)}` : 'Recorded on your account.',
      time: timeAgo(tx.date),
      link: '/transactions',
    })
  })

  const lastTrade = user.trades?.[user.trades.length - 1]
  if (lastTrade) {
    const profit = lastTrade.tradeType === 'profit'
    items.push({
      id: `trade-${lastTrade.date}-${lastTrade.pair}-${lastTrade.amount}`,
      kind: 'activity',
      tone: profit ? 'success' : 'danger',
      title: `Trade ${profit ? 'profit' : 'loss'} on ${lastTrade.pair || 'a position'}`,
      text: `${profit ? '+' : '-'}${usd(lastTrade.amount)} from your copied trader.`,
      time: lastTrade.date || '',
      link: '/usercopytrade',
    })
  }

  // ── account status ──
  const kyc = user.kycStatus
  if (kyc === 'processing') {
    items.push({ id: 'kyc-processing', kind: 'status', tone: 'info', title: 'KYC under review',
      text: "We're checking your documents. Withdrawals unlock once approved.", link: '/kyc' })
  } else if (kyc === 'rejected') {
    items.push({ id: `kyc-rejected-${user.kycRejectionReason || ''}`, kind: 'status', tone: 'danger', title: 'KYC needs attention',
      text: user.kycRejectionReason ? `Reason: ${user.kycRejectionReason}. Please resubmit.` : 'Your verification was declined. Please resubmit.', link: '/kyc' })
  } else if (kyc === 'approved') {
    items.push({ id: 'kyc-approved', kind: 'status', tone: 'success', title: 'Identity verified',
      text: 'Your KYC is approved. Withdrawals are unlocked.', link: '/kyc' })
  } else {
    items.push({ id: 'kyc-required', kind: 'status', tone: 'warning', title: 'Verify your identity',
      text: 'Complete KYC to unlock withdrawals.', link: '/kyc' })
  }

  if (Number(user.funded || 0) === 0) {
    items.push({ id: 'balance-empty', kind: 'status', tone: 'warning', title: 'Your balance is empty',
      text: 'Make a deposit to activate a plan and start copy trading.', link: '/fundwallet' })
  } else if (!user.trader) {
    items.push({ id: 'no-trader', kind: 'status', tone: 'warning', title: "You're not copying anyone yet",
      text: 'Pick an expert trader so their trades mirror into your account.', link: '/traders' })
  }

  // ── tips ──
  if (!user.twoFactorEnabled) {
    items.push({ id: 'tip-2fa', kind: 'tip', tone: 'tip', title: 'Tip: turn on 2FA',
      text: 'Protect your account with a code from an authenticator app at every sign-in.', link: '/2fa' })
  }
  if (!(user.referred?.length > 0)) {
    items.push({ id: 'tip-referral', kind: 'tip', tone: 'tip', title: 'Tip: earn with referrals',
      text: 'Share your link and earn 10% of every deposit your referrals make.', link: '/referrals' })
  }

  return items
}

// ── read state ──
const storageKey = (user) => `ud-notifications-read:${user?.email || 'anon'}`

export const loadReadIds = (user) => {
  try { return new Set(JSON.parse(localStorage.getItem(storageKey(user)) || '[]')) }
  catch { return new Set() }
}

export const saveReadIds = (user, ids) => {
  try { localStorage.setItem(storageKey(user), JSON.stringify([...ids])) } catch { /* storage unavailable */ }
}
